import { useCallback, useEffect, useRef, useReducer } from 'react';
import {
  useSearchFetchAll,
  useSearchFetchFiles,
  useSearchFetchFolders,
  useSearchReset,
} from '@/containers/WorkspaceGlobalSearch/hooks';
import { useSearchFiltersQuery } from '@/containers/WorkspaceGlobalSearch/hooks/useSearchFilterQuery';
import { ReducedFiltersContext } from './Context';
import type { IMainSearchFilters } from './interfaces';
import { SearchMainFilters } from './Search.Main.Filters';
import * as FilterState from './Search.Main.Filters.state';
import SearchTabView from './Search.Tab.View';
import styles from './style/Main.module.css';

export default function SearchMain() {
  const reset = useSearchReset();
  const fetchAll = useSearchFetchAll();
  const fetchFiles = useSearchFetchFiles();
  const fetchFolders = useSearchFetchFolders();

  const [query] = useSearchFiltersQuery();
  const [filters, dispatch] = useReducer(FilterState.reducer, FilterState.getInitialState(query));
  const initialized = useRef<number>(0);

  const fetcher = useCallback(({ filters: { tab, keywords, filetypes, tags } }: FetcherData) => {
    const filters = {
      keywords,
      filetypes,
      tags,
    };

    switch (tab) {
      case 'all': fetchAll({ filters }); break;
      case 'files': fetchFiles({ filters, size: 50 }); break;
      case 'folders': fetchFolders({ filters, size: 50 }); break;
    }

  }, [fetchAll, fetchFiles, fetchFolders]);

  useEffect(() => {
    dispatch({
      type: 'query-update',
      value: {
        tab: query.tab,
        keywords: query.keywords,
        filetypes: query.filetypes,
        tags: query.tags,
      },
    });
  }, [
    query.tab,
    query.keywords,
    query.filetypes,
    query.tags,
  ]);

  useEffect(() => {
    if (initialized.current < 1) { // skip first load
      initialized.current++;
      return;
    }
    reset(filters.tab);
    fetcher({ filters });
  }, [reset, fetcher, filters]);

  return (
    <div className={styles.root}>
      <div className={styles.wrap}>
        <ReducedFiltersContext.Provider value={filters}>
          <div className={styles.filters}>
            <SearchMainFilters />
          </div>
          <div className={styles.main}>
            <SearchTabView />
          </div>
        </ReducedFiltersContext.Provider>
      </div>
    </div>
  );
}

export { SearchMain };

type FetcherData = {
  filters: IMainSearchFilters.State;
};